// DEFAULT_COLOR is shared with the legend's fallback so the two cannot drift:
// "#000" and "black" render the same but read as two independent decisions.
import {
  DEFAULT_AXIS_FONT_SIZE,
  DEFAULT_AXIS_LABEL_FONT_SIZE,
  DEFAULT_COLOR
} from "constants/chart.constants";
import { THEME_FONT_FAMILY } from "constants/style.constants";
import { EChartsOption } from "echarts";
import { wellStatusDisplayName } from "entities/charts/eva/requests/wellStatus";
import { DEFAULT_TITLE_OPTIONS } from "entities/charts/options/title";
import { EvaChartStates } from "types/factory";
import { removeCount } from "utils";

import {
  AXIS_LABEL_COLOR,
  AXIS_TITLE_COLOR,
  DEFAULT_Z_INDEX,
  VALUE_AXIS_LINE
} from "./constants";
import {
  integerValueAxisOptions,
  resolveSeriesColorIgnoringCount,
  rigFracTooltip
} from "./rigFracActivity";
import { recentCategoryLabelWidth } from "./rigFracRecentActivity";
import { buildWellStatusGliders } from "./wellStatusGliders";
import {
  buildWellStatusItemTooltipFormatter,
  stackBucketTotals
} from "./wellStatusTooltip";

// Plot-area insets. Shared by both modes so the gliders and the grid agree.
// 56, matching the over-time chart: both draw a VISIBLE title, and
// DEFAULT_TITLE_OPTIONS reserves 14px above and 10px below an 18px line.
/**
 * Plot-area top inset. Exported so the test asserts against this value rather
 * than a copied literal — the assertion exists to catch drift, and a hardcoded
 * number cannot.
 */
export const CURRENT_GRID_TOP = 56;
const GRID_BOTTOM = 48;

/** Value (x) axis title size. Exported so WellStatusSelector's x-axis button matches it. */
export const CURRENT_VALUE_AXIS_TITLE_FONT_SIZE = 14;

/**
 * Everything the two modes DON'T share. The rest of the chart — grid, gliders,
 * both axes, tooltip chrome — is identical between them, so it lives once in
 * `buildHorizontalStatusOptions` below.
 */
interface HorizontalStatusParts {
  /** Category-axis labels, top to bottom (the server already ranks them). */
  categories: string[];
  /** Stack totals per bin for "(of N)"; omitted for single-value bars. */
  totals?: number[];
  /** Fixed status label for the value row; omitted when the series is the status. */
  metricLabel?: string;
  series: EChartsOption["series"];
}

/**
 * The shared chart shell for both Current Well Status modes: horizontal ranked
 * bars, largest at top, integer value axis, gliders on both axes.
 *
 * Extracted because the two modes' option objects were duplicated verbatim apart
 * from the four fields in `HorizontalStatusParts` — so a spacing or screenshot
 * fix applied to one silently missed the other.
 *
 * `labelWidth` derives from `categories` here rather than being passed in, which
 * is what keeps `grid.left`, `yAxis.nameGap` and `axisLabel.width` consistent
 * with each other; the WellStatusSelector reads `grid.left` back at runtime to
 * stay centred on the value-axis title.
 */
function buildHorizontalStatusOptions(
  { categories, totals, metricLabel, series }: HorizontalStatusParts,
  states: Partial<EvaChartStates>,
  groupTitle: string,
  countTitle: string,
  chartTitle?: string
): EChartsOption {
  const { title, chartType } = states;
  const labelWidth = recentCategoryLabelWidth(categories, DEFAULT_AXIS_LABEL_FONT_SIZE);
  const gridLeft = labelWidth + 32;

  return {
    // MUST be emitted. The screenshot transform keys its lean-widget path off
    // `options.chartType`; without it this chart fell through to the WELLS
    // grid/nameGap/spacing overrides and its fixed grid was re-measured against
    // the chart instance.
    chartType,
    // Inherited by every piece of text ECharts draws — axis labels, axis names,
    // the title — the way the wells charts get it from DEFAULT_CHART_OPTIONS.
    // These translators build their options from scratch, so without it the chart
    // fell back to the browser's default sans and read differently from every
    // other chart on the dashboard.
    textStyle: { fontFamily: THEME_FONT_FAMILY },
    // No bar transitions anywhere in this widget (product); also what
    // DEFAULT_CHART_OPTIONS does for the wells charts.
    animation: false,
    // Visible, and styled by DEFAULT_TITLE_OPTIONS so it is the same 18px bold
    // #041C2C, left-aligned with the same padding, as the over-time chart and the
    // wells charts. The chart previously built a title and then set show:false, so
    // this widget was the only one on a dashboard without a heading.
    title: {
      ...DEFAULT_TITLE_OPTIONS,
      text: chartTitle ?? title ?? "",
      show: true
    },
    grid: {
      top: CURRENT_GRID_TOP,
      left: gridLeft,
      right: 40,
      bottom: GRID_BOTTOM,
      containLabel: false
    },
    // Gliders: x zooms the count axis, y SCROLLS the ranked focus bins — which
    // is what makes a 20-bin focus readable.
    dataZoom: buildWellStatusGliders({
      top: CURRENT_GRID_TOP,
      left: gridLeft,
      bottom: GRID_BOTTOM
    }),
    tooltip: {
      ...rigFracTooltip,
      trigger: "item",
      axisPointer: { type: "none" },
      formatter: buildWellStatusItemTooltipFormatter({
        groupByLabel: groupTitle,
        groupFrom: "category",
        metricLabel,
        totals
      })
    },
    xAxis: {
      // Value axis is horizontal here.
      type: "value",
      name: countTitle,
      nameLocation: "middle",
      nameGap: 28,
      nameTextStyle: {
        fontSize: CURRENT_VALUE_AXIS_TITLE_FONT_SIZE,
        color: AXIS_TITLE_COLOR
      },
      ...integerValueAxisOptions,
      // Same reason as the over-time chart's y-axis: ECharts draws an axis line on
      // a CATEGORY axis by default but not on a value one. The bars are horizontal
      // here, so it is the X axis that needs it — without this the count axis had
      // no line while the category axis beside it did.
      axisLine: VALUE_AXIS_LINE,
      // Explicit objects so the screenshot Major/Minor toggles have something to
      // flip — the transform only rewrites `splitLine.show` when it already
      // exists.
      splitLine: { show: true },
      minorTick: { show: false },
      minorSplitLine: { show: false, lineStyle: { color: "#F2F2F2" } },
      // max deliberately UNSET, matching the over-time chart's value axis: pinning
      // it to the data max ends the axis on 65,961 and runs the longest bar flat
      // into the plot edge. Left open, ECharts rounds up to the next whole tick
      // (70,000) so the bar reads against a round number and has room to breathe.
      // `min: 0` + minInterval 1 (integerValueAxisOptions) are what make that safe
      // — a whole-numbered axis that cannot drift negative or degenerate on zeros.
      min: 0
    },
    yAxis: {
      type: "category",
      data: categories,
      // Largest at the top.
      inverse: true,
      name: groupTitle,
      nameLocation: "middle",
      nameGap: labelWidth + 16,
      nameRotate: 90,
      nameTextStyle: { fontSize: DEFAULT_AXIS_FONT_SIZE, color: AXIS_TITLE_COLOR },
      axisLabel: {
        fontSize: DEFAULT_AXIS_LABEL_FONT_SIZE,
        // As on the over-time chart: the value axis inherits this from
        // integerValueAxisOptions, the category axis has to state it.
        color: AXIS_LABEL_COLOR,
        width: labelWidth,
        overflow: "truncate"
      },
      // Off by default — a category axis draws no gridlines — but PRESENT so the
      // Y-Axis Major toggle is not a silent no-op.
      splitLine: { show: false },
      axisTick: { alignWithLabel: true }
    },
    series
  } as EChartsOption;
}

/**
 * Current Well Status. HORIZONTAL bars, one per focus bin, sorted
 * descending (largest at top via `yAxis.inverse`) — the same shape and spacing as
 * the rig/frac Recent Activity chart, which is what the ticket asks for
 * ("similar to recent activity chart for rigs/frac").
 *
 * Reuses that chart's adaptive category-label width (an EVA-76 follow-up fix: the
 * band sizes to the 75th-percentile label so one long name can't strand a wide
 * empty gap) plus the shared integer-axis and colour helpers.
 */
export function translateWellStatusCurrentResponseToOptions(
  states: Partial<EvaChartStates>,
  focusTitle?: string,
  isAllStatuses = false,
  statusTitle?: string,
  chartTitle?: string
): EChartsOption {
  // `title` and `chartType` are read by the shared shell, not here.
  const { layout, series, seriesColors } = states;

  if (!series || series.length === 0) {
    return {};
  }

  if (isAllStatuses) {
    return buildStackedOptions(states, focusTitle, statusTitle, chartTitle);
  }

  // Each response series is one focus bin with a single value (y[0]).
  // Ranked by count, DELIBERATELY overriding the legend order that
  // useWellStatusChartOptions applied upstream — a ranked horizontal bar chart is
  // what the ticket asks for ("similar to recent activity chart"), and its whole
  // point is that the longest bar is on top.
  //
  // So this chart's bar order and its data table's row order differ by design:
  // the table follows the legend, as the over-time table does. That difference is
  // intentional, not the header-ordering bug this widget already fixed elsewhere.
  const groups = series
    .map((s) => ({
      // Product asked to drop the "(N)" well-count prefix from the axis labels (EVA-302).
      label: removeCount(String(s.label ?? "")),
      value: Number(s.y?.[0]) || 0,
      color: resolveSeriesColorIgnoringCount(
        s.label,
        seriesColors,
        s.style?.hexColor ?? DEFAULT_COLOR
      )
    }))
    .sort((a, b) => b.value - a.value);

  // The backend emits a VERTICAL-bar layout (category on x, value on y), so its
  // axis titles are for that orientation: xAxis.title = the group dimension,
  // yAxis.title = the count. This translator flips to horizontal, so the titles
  // swap axes too — otherwise the value axis would read "Header.Basin" and the
  // category axis "PROD Count".
  //
  // The server can only name the group dimension by its raw property
  // ("Header.ResourcePlay") — the human label lives client-side on the focus's
  // IGroupBy.title ("Resource Play"), so the caller passes it in and it wins.
  const groupTitle = focusTitle || layout?.xAxis?.title || "";
  // The status's human name ("Drilled Uncompleted"), NOT the server's
  // "<abbrev> Count" — the bars are already counts. Stacked mode keeps the
  // server's "Well Count", since there the length is a total across statuses.
  const countTitle = statusTitle || layout?.yAxis?.title || "";

  return buildHorizontalStatusOptions(
    {
      categories: groups.map((g) => g.label),
      metricLabel: countTitle,
      series: [
        {
          type: "bar",
          // Tooltip rows read seriesName — without one ECharts falls back to its
          // internal "series0", which is what a hovered bar used to show. Naming
          // it by the METRIC makes a hover read "<bin> — PROD Count: 1,169".
          name: countTitle || "Count",
          // Per-bar colours ride the data items (one series, many colours) — the
          // legend is fed separately from the raw response series.
          data: groups.map((g) => ({
            value: g.value,
            name: g.label,
            itemStyle: { color: g.color }
          })),
          z: DEFAULT_Z_INDEX
        }
      ]
    },
    states,
    groupTitle,
    countTitle,
    chartTitle
  );
}

/**
 * "All Statuses" variant: the same horizontal ranked bars, but each bar SPLIT into
 * one segment per status rather than being a single filtered count.
 *
 * The response shape differs from the filtered mode's, because the two colour by
 * different things: here the server sends one series per STATUS, each carrying
 * that status's count for every bin in bin order, so `layout.xAxis.data` is the
 * category axis and every series stacks onto it. Colours (and therefore the
 * legend) key off status, not focus bin.
 */
function buildStackedOptions(
  states: Partial<EvaChartStates>,
  focusTitle?: string,
  statusTitle?: string,
  chartTitle?: string
): EChartsOption {
  const { layout, series, seriesColors } = states;

  // Bin order comes from the server (ranked by total); `inverse` then draws the
  // largest at the TOP rather than echarts' default bottom-up.
  const binLabels = (layout?.xAxis?.data ?? []).map((d) => removeCount(String(d ?? "")));
  const groupTitle = focusTitle || layout?.xAxis?.title || "";
  // The status's human name ("Drilled Uncompleted"), NOT the server's
  // "<abbrev> Count" — the bars are already counts. Stacked mode names the
  // SELECTION ("All Statuses"), since there the length is a total across statuses.
  const countTitle = statusTitle || layout?.yAxis?.title || "";

  const statusSeries = (series ?? []).map((s) => ({
    type: "bar" as const,
    // Series name drives the legend and the tooltip rows. chart-svc sends the
    // bare abbreviation ("PROD"); show the readable name instead.
    name: wellStatusDisplayName(String(s.label ?? "")),
    stack: "wellStatus",
    data: binLabels.map((_, index) => Number(s.y?.[index]) || 0),
    itemStyle: {
      color: resolveSeriesColorIgnoringCount(
        s.label,
        seriesColors,
        s.style?.hexColor ?? DEFAULT_COLOR
      )
    },
    z: DEFAULT_Z_INDEX
  }));

  return buildHorizontalStatusOptions(
    {
      categories: binLabels,
      totals: stackBucketTotals(series ?? [], binLabels.length).rounded,
      series: statusSeries
    },
    states,
    groupTitle,
    countTitle,
    chartTitle
  );
}
